// How the group's own worker image stands (D90): not built yet (the first task that needs it
// builds it), building (tasks wait), ready (its tag), or failed with the build's output and
// Rebuild, which forgets the failure so the next task builds again.
import { ArrowClockwise, CheckCircle, HourglassMedium, WarningCircle } from "@phosphor-icons/react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "rizzui";

import { useT } from "../../i18n";
import { toast } from "../../layout/toast";
import { OutputPeek } from "../../ui/OutputPeek";
import { Mono } from "../../ui/status";
import { dayTime } from "../../workloads/format";
import { type GroupImage, rebuildImage, toolchainsQuery } from "./api";

export function ImageStatus({ groupId, image, canRebuild }: { groupId: string; image: GroupImage | null; canRebuild: boolean }) {
  const t = useT().tasks.toolchains;
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  async function rebuild() {
    setBusy(true);
    try {
      queryClient.setQueryData(toolchainsQuery(groupId).queryKey, await rebuildImage(groupId));
      toast(t.rebuildQueued);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), "warn");
    } finally {
      setBusy(false);
    }
  }

  if (!image) return <p className="m-0 text-[13px] text-gray-500">{t.notBuilt}</p>;
  if (image.state === "building") {
    return (
      <p className="m-0 flex items-start gap-2 text-[13px] text-blue-dark" role="status">
        <HourglassMedium weight="bold" className="mt-0.5 h-4 w-4 shrink-0 animate-pulse" aria-hidden /> {t.building(dayTime(image.at))}
      </p>
    );
  }
  if (image.state === "ready") {
    return (
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-green-dark">
        <span className="flex items-center gap-1.5">
          <CheckCircle weight="fill" className="h-4 w-4" aria-hidden /> {t.ready(dayTime(image.at))}
        </span>
        <Mono className="truncate text-[11.5px] text-gray-500">{image.tag}</Mono>
      </div>
    );
  }
  return (
    <div className="space-y-2 rounded-lg border border-red/30 bg-red-lighter/40 p-3">
      <p className="m-0 flex items-start gap-1.5 text-[13px] font-medium text-red-dark">
        <WarningCircle weight="fill" className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /> {t.failed(dayTime(image.at))}
      </p>
      {image.reason ? <OutputPeek kind="error" text={image.reason} title={t.buildOutput} /> : null}
      {canRebuild ? (
        <Button size="sm" variant="outline" className="gap-1.5" isLoading={busy} onClick={() => void rebuild()}>
          <ArrowClockwise weight="bold" className="h-3.5 w-3.5" aria-hidden /> {t.rebuild}
        </Button>
      ) : null}
    </div>
  );
}
